import { Head, Link, router, useForm } from '@inertiajs/react'
import { ArrowLeft, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { batchPath, batchesPath } from '@/lib/routes'

import { BatchFormFields, type BatchFormData } from './_Form'

type EditBatch = {
  id: number
  code: string
  year: number
  period: 1 | 2
}

type BatchesEditProps = {
  batch: EditBatch
}

function BatchesEdit({ batch }: BatchesEditProps) {
  const form = useForm<BatchFormData>({
    code: batch.code,
    year: batch.year,
    period: batch.period,
  })

  return (
    <>
      <Head title={`${batch.code} — Batches`} />

      <div className="mx-auto max-w-3xl px-8 py-10">
        <Link
          href={batchesPath()}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to batches
        </Link>

        <PageHeader title={batch.code} />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ batch: data }))
            form.patch(batchPath(batch.id))
          }}
        >
          <BatchFormFields
            data={form.data}
            errors={form.errors as Partial<Record<keyof BatchFormData, string>>}
            setData={form.setData}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Link href={batchesPath()}>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>

        <Card className="mt-6 border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Delete batch</CardTitle>
            <CardDescription>
              Only works if no companies are currently in this batch.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => {
                if (window.confirm(`Delete ${batch.code}?`)) {
                  router.delete(batchPath(batch.id))
                }
              }}
            >
              <Trash2 />
              Delete
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(BatchesEdit, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
